import { useMemo, useState } from 'react'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import Typography from '@mui/material/Typography'
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage'
import FoodBankIcon from '@mui/icons-material/FoodBank'
import WorkspacesIcon from '@mui/icons-material/Workspaces'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ListIcon from '@mui/icons-material/List'
import { useTranslation } from 'react-i18next'
import { useParams, useLoaderData, useNavigate, Outlet } from 'react-router-dom'

import { LoaderData, ParticipantStatus, EventType, EventStatus } from '../../types'
import ParticipantStatusTag from '../events/components/ParticipantStatusTag'
import TimeGridCalendar from '../events/view/components/TimeGridCalendar'
import ResourceGridCalendar from '../events/view/components/ResourceGridCalendar'
import { CalendarData } from '../events/view/components/useCalendarData'
import { useFilterParticipantOptions } from './components/filterParticipantOptions'
import RegisterDetails from './components/RegisterDetails'
import SponsorshipDetails from './components/SponsorshipDetails'
import type { agendaDetailsLoader } from './Agenda.loader'
import TourCalendar from '../events/view/components/TourCalendar'
import IconButtonOutlined from '../root/components/IconButtonOutlined'
import ListCalendar from '../events/view/components/ListCalendar'

export default function AgendaDetails() {
  const { t } = useTranslation('dashboard')
  const { eventId } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [viewType, setViewType] = useState<'list' | 'calendar'>('list')

  const eventDetails = useLoaderData() as LoaderData<ReturnType<typeof agendaDetailsLoader>>

  if (!eventId || Number.isNaN(Number(eventId)))
    throw new Error(t('Impossible de récupérer vos informations d’inscription.'))

  const calendarData: CalendarData = useMemo(
    () => ({
      event: eventDetails.event,
      sessions: eventDetails.agenda.sessions,
      subEvents: eventDetails.agenda.sub_events,
    }),
    [eventDetails.agenda, eventDetails.event]
  )

  const registrationOptions = useFilterParticipantOptions(
    eventDetails.participation,
    eventDetails.subEvents,
    eventDetails.sessions,
    eventDetails.event
  )

  return (
    <Stack width="100%" height="100%" direction="column" padding="1rem">
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap="1rem">
        <Typography variant="h2">
          {t('Inscription')} : {calendarData.event.name}
        </Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap="0.5rem">
          <ParticipantStatusTag status={calendarData.event?.registration_status} />

          {!eventDetails.participation && (
            <LoadingButton
              onClick={e => {
                e.preventDefault()
                navigate(`/events/${eventId}/register`)
                setLoading(true)
              }}
              href={`/events/${eventId}/register`}
              variant={
                eventDetails.event.status !== EventStatus.RegistrationOpen
                  ? 'outlined'
                  : 'contained'
              }
              loading={loading}
              disabled={eventDetails.event.status !== EventStatus.RegistrationOpen}
            >
              {eventDetails.event.status === EventStatus.RegistrationOpen
                ? t("S'inscrire")
                : t('Inscription fermée')}
            </LoadingButton>
          )}
          {!!eventDetails.participation &&
            eventDetails.participation.status !== ParticipantStatus.Confirmed && (
              <LoadingButton
                onClick={e => {
                  e.preventDefault()
                  navigate(`/events/${eventId}/register`)
                  setLoading(true)
                }}
                href={`/events/${eventId}/register`}
                variant="contained"
                loading={loading}
              >
                {eventDetails.participation.status === ParticipantStatus.Cancelled ||
                eventDetails.participation.status === ParticipantStatus.CheckoutFailed
                  ? t('Renouveler mon inscription')
                  : t('Poursuivre mon inscription')}
              </LoadingButton>
            )}
        </Stack>
      </Stack>

      <Divider />

      {!!eventDetails.sponsorship && (
        <SponsorshipDetails
          sponsorship={eventDetails.sponsorship}
          sponsorshipType={eventDetails.sponsorshipTypes}
        />
      )}

      {!!eventDetails.participation && (
        <>
          {eventDetails.participation?.status === ParticipantStatus.Cancelled && (
            <Typography variant="h4" color="error">
              {t('Le remboursement de votre inscription sera effectué sous quelques jours.')}
            </Typography>
          )}

          <Stack direction="row" alignItems="center" justifyContent="start" marginTop="1rem">
            <Typography
              variant="h4"
              width="fit-content"
              sx={theme => ({ borderBottom: `2px solid ${theme.palette.divider}` })}
              marginBottom="0.2rem"
            >
              {t('Inscriptions complémentaires')} :
            </Typography>
          </Stack>

          <Stack direction="column" gap="0.5rem">
            {!eventDetails.participation.participant_options?.length && (
              <Typography>{t("Vous n'avez aucune inscription complémentaire")}</Typography>
            )}
            {!!registrationOptions?.hotelOptions?.length && (
              <Stack
                direction="column"
                border={theme => `1px solid ${theme.palette.divider}`}
                borderRadius="0.5rem"
                padding="0.1rem 0.5rem"
              >
                <Stack direction="row" alignItems="center" gap="0.5rem">
                  <HolidayVillageIcon />
                  <h6>{t('Hébergements')}</h6>
                </Stack>
                <Stack gap="1rem" marginLeft="2rem">
                  {registrationOptions?.hotelOptions.map(elem => (
                    <RegisterDetails key={elem.id} participantOptions={elem} />
                  ))}
                </Stack>
              </Stack>
            )}
            {registrationOptions?.mealOptions?.length ? (
              <Stack
                direction="column"
                border={theme => `1px solid ${theme.palette.divider}`}
                borderRadius="0.5rem"
                padding="0.1rem 0.5rem"
              >
                <Stack direction="row" alignItems="center" gap="0.5rem">
                  <FoodBankIcon />
                  <h6>{t('Repas')}</h6>
                </Stack>
                <Stack gap="0.5rem" marginLeft="2rem">
                  {registrationOptions?.mealOptions.map(elem => (
                    <RegisterDetails key={elem.id} participantOptions={elem} />
                  ))}
                </Stack>
              </Stack>
            ) : null}
            {registrationOptions?.sessionOptions?.length ? (
              <Stack
                direction="column"
                border={theme => `1px solid ${theme.palette.divider}`}
                borderRadius="0.5rem"
                padding="0.1rem 0.5rem"
              >
                <Stack direction="row" alignItems="center" gap="0.5rem">
                  <WorkspacesIcon />
                  <h6>{t('Séances')}</h6>
                </Stack>
                <Stack gap="0.5rem" marginLeft="2rem">
                  {registrationOptions?.sessionOptions.map(elem => (
                    <RegisterDetails key={elem.id} participantOptions={elem} />
                  ))}
                </Stack>
              </Stack>
            ) : null}
            {registrationOptions?.workshopOptions?.length ? (
              <Stack
                direction="column"
                border={theme => `1px solid ${theme.palette.divider}`}
                borderRadius="0.5rem"
                padding="0.1rem 0.5rem"
              >
                <Stack direction="row" alignItems="center" gap="0.5rem">
                  <WorkspacesIcon />
                  <h6>{t('Ateliers')}</h6>
                </Stack>
                <Stack gap="0.5rem" marginLeft="2rem">
                  {registrationOptions?.workshopOptions.map(elem => (
                    <RegisterDetails key={elem.id} participantOptions={elem} />
                  ))}
                </Stack>
              </Stack>
            ) : null}
            {registrationOptions?.subEventOptions?.length ? (
              <Stack
                direction="column"
                border={theme => `1px solid ${theme.palette.divider}`}
                borderRadius="0.5rem"
                padding="0.1rem 0.5rem"
              >
                <Stack direction="row" alignItems="center" gap="0.5rem">
                  <WorkspacesIcon />
                  <h6>{t('Inscriptions complémentaires')}</h6>
                </Stack>
                <Stack gap="0.5rem" marginLeft="2rem">
                  {registrationOptions?.subEventOptions.map(elem => (
                    <RegisterDetails key={elem.id} participantOptions={elem} />
                  ))}
                </Stack>
              </Stack>
            ) : null}

            {calendarData.event?.type === EventType.Bootcamp &&
              registrationOptions.sessionOptions.length === 0 && (
                <Stack
                  direction="column"
                  border={theme => `1px solid ${theme.palette.divider}`}
                  borderRadius="0.5rem"
                  padding="0 0.5rem"
                >
                  <Stack direction="row" alignItems="center" gap="0.5rem" paddingTop="1rem">
                    <WorkspacesIcon />
                    <Typography variant="h5">
                      {t(
                        'Vous n’êtes inscrit à aucune séance pour le moment, un programme personnalisé vous sera assigné bientôt'
                      ) + '.'}
                    </Typography>
                  </Stack>
                </Stack>
              )}
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            margin="2rem 0 1rem"
          >
            <Stack direction="row" gap="1rem" alignItems={'center'}>
              <Typography
                variant="h4"
                width="fit-content"
                sx={theme => ({ borderBottom: `2px solid ${theme.palette.divider}` })}
              >
                {t('Calendrier')} :
              </Typography>
              <Button
                size="small"
                onClick={e => {
                  e.preventDefault()
                  navigate(`/events/${eventId}`)
                }}
                href="/events/${eventId}"
                variant="outlined"
              >
                {t('Voir le programme complet')}
              </Button>
            </Stack>
            <Stack direction="row" gap="0.2rem">
              <IconButtonOutlined
                color={viewType === 'list' ? 'primary' : undefined}
                disabled={viewType === 'list'}
                onClick={() => {
                  setViewType('list')
                }}
              >
                <ListIcon />
              </IconButtonOutlined>
              <IconButtonOutlined
                color={viewType === 'calendar' ? 'primary' : undefined}
                disabled={viewType === 'calendar'}
                onClick={() => {
                  setViewType('calendar')
                }}
              >
                <CalendarMonthIcon />
              </IconButtonOutlined>
            </Stack>
          </Stack>

          {calendarData ? (
            (function renderCalendarType() {
              switch (viewType) {
                case 'list':
                  return (
                    <ListCalendar
                      data={calendarData}
                      showHiddenFromGenericProgram
                      showOnlySessions={eventDetails.event.type === EventType.Congress}
                    />
                  )
                case 'calendar':
                default:
                  if (eventDetails.event.type === EventType.Congress) {
                    return <ResourceGridCalendar data={calendarData} showHiddenFromGenericProgram />
                  }
                  if (eventDetails.event.type === EventType.Tour) {
                    return <TourCalendar data={calendarData} showHiddenFromGenericProgram />
                  }
                  return <TimeGridCalendar data={calendarData} showHiddenFromGenericProgram />
              }
            })()
          ) : (
            <Typography variant="h1" color="error">
              {t(
                "Impossible d'afficher le calendrier, veuillez recharger la page ou contacter le support"
              )}
              .
            </Typography>
          )}
        </>
      )}

      <Stack paddingBottom="1rem" />

      <Outlet />
    </Stack>
  )
}
